(() => {
  // voorkom dubbele initialisatie (bijv. door Quick Add modal script reinjection)
  if (window.__ProductSurveyLoaded) return;
  window.__ProductSurveyLoaded = true;

  class ProductSurvey extends HTMLElement {
    static VARIANT_TITLES = {
      vaginaal: "Vaginale test",
      urine: "Urinetest",
      oraal: "Orale test",
      anaal: "Anale test",
    };

    constructor() {
      super();
      this.jsonUrl = this.dataset.jsonUrl;
      const productDataEl = this.querySelector(
          `#ProductData-${this.dataset.section}`,
      );
      if (!productDataEl) {
        console.warn(
            "[ProductSurvey] ProductData script tag niet gevonden in component",
            this.dataset.section,
        );
        return;
      }
      this.productData = JSON.parse(productDataEl.textContent);

      this.stepContainer = this.querySelector(".survey-step-container");
      this.introContainer = this.querySelector(".survey-intro");
      this.loading = this.querySelector(".survey-loading");
      this.resultsContainer = this.querySelector(".survey-results");
      this.variantsContainer = this.querySelector(".survey-results__list");
      this.informationContainer = this.querySelector(".survey-information");

      this.surveyForm = this.querySelector("form");
      this.variantInputsContainer = this.querySelector(
          "#SurveyVariantInputs-" + this.dataset.section,
      );
      this.addToCartBtn = this.querySelector(
          "#AddToCartBtn-" + this.dataset.section,
      );
      this.buyNowBtn = this.querySelector("#BuyNowBtn-" + this.dataset.section);
      this.productForm = this.querySelector(".survey-product-form");
      this.requiredMsg = this.querySelector(
          "#SurveyRequiredMsg-" + this.dataset.section,
      );

      this.hideErrors = this.dataset.hideErrors === "true";
      this.surveyData = null;
      this.currentStep = 0;
      this.answers = {};
      this.selectedVariants = [];
      this.isSingleStep = false;
      this.__purchaseAttempted = false;
      this.__manualEmptyMsg =
          "Selecteer één of meer testen.";
      this.__originalNoResultsText = "";

      this.init();
      this.patchCartNotification();
    }

    connectedCallback() {
      if (this.__initialized) return;
      console.log("[ProductSurvey] connected", this.dataset.section, this);

      this.setupTouchTooltipBehavior();

      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener("click", (e) => {
          e.preventDefault();
          if (!this.canPurchase()) {
            this.__purchaseAttempted = true;
            this.nudgeSurveyRequired();
            return;
          }
          this.addToCart();
        });
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener("click", (e) => {
          e.preventDefault();
          if (!this.canPurchase()) {
            this.__purchaseAttempted = true;
            this.nudgeSurveyRequired();
            return;
          }
          this.buyNow();
        });
      }

      const resetBtn = this.querySelector(".survey-results__reset-btn");
      if (resetBtn) {
        resetBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.resetSurvey();
        });
      }

      const manualBtn = this.querySelector(
          "#ManualPickerBtn-" + this.dataset.section,
      );
      if (manualBtn) {
        manualBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.showManualPicker();
        });
      }

      this.__initialized = true;
    }

    setupTouchTooltipBehavior() {
      // Alleen nodig op touch/coarse pointers; desktop gebruikt :hover
      try {
        const isTouch =
            window.matchMedia &&
            window.matchMedia("(hover: none), (pointer: coarse)").matches;
        if (!isTouch) return;
      } catch (e) {
        return;
      }

      const openClass = "survey-option--tooltip-open";
      const alignLeftClass = "tooltip-align-left";
      const alignRightClass = "tooltip-align-right";
      let closeTimer = null;

      const closeAll = () => {
        this.querySelectorAll(`.${openClass}`).forEach((el) => {
          el.classList.remove(openClass);
          el.classList.remove(alignLeftClass);
          el.classList.remove(alignRightClass);
        });
        // Sluit ook discount icon popovers
        this.querySelectorAll(".survey-variant__discount-icon.active").forEach((el) => {
          el.classList.remove("active");
        });
      };

      const applyTooltipAlignment = (optionEl) => {
        if (!optionEl) return;

        optionEl.classList.remove(alignLeftClass);
        optionEl.classList.remove(alignRightClass);

        const rect = optionEl.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth;
        if (!vw) return;

        // Tooltip heeft width: min(34ch, 90vw). We nemen 90vw als worst-case om overflow te voorkomen.
        const tooltipWidth = Math.min(vw * 0.9, vw);
        const center = rect.left + rect.width / 2;
        const margin = 12;

        if (center - tooltipWidth / 2 < margin) {
          optionEl.classList.add(alignLeftClass);
        } else if (center + tooltipWidth / 2 > vw - margin) {
          optionEl.classList.add(alignRightClass);
        }
      };

      // Capture phase zodat we ook clicks op disabled inputs/labels kunnen afvangen
      this.addEventListener(
          "click",
          (e) => {
            const optionEl = e.target?.closest?.(
                ".survey-option--tooltip[data-tooltip]",
            );

            if (!optionEl || !this.contains(optionEl)) {
              closeAll();
              return;
            }

            const input = optionEl.querySelector(
                'input[type="checkbox"], input[type="radio"]',
            );

            // Tooltip wordt hier gebruikt voor disabled opties; alleen dan openen
            if (input && input.disabled !== true) return;

            e.preventDefault();
            e.stopPropagation();

            const shouldOpen = !optionEl.classList.contains(openClass);
            closeAll();
            if (shouldOpen) {
              optionEl.classList.add(openClass);
              applyTooltipAlignment(optionEl);
            }

            if (closeTimer) window.clearTimeout(closeTimer);
            closeTimer = window.setTimeout(() => closeAll(), 3500);
          },
          true,
      );

      document.addEventListener(
          "click",
          (e) => {
            if (!this.contains(e.target)) closeAll();
          },
          true,
      );
    }

    async init() {
      const spinningLoader = this.loading
          ? this.loading.querySelector(".loading__spinner")
          : null;
      if (spinningLoader) spinningLoader.classList.remove("hidden");

      try {
        const response = await fetch(this.jsonUrl);
        this.surveyData = await response.json();

        // Altijd multi-step variant gebruiken (single-step is uitgeschakeld)
        this.isSingleStep = false;
        this.classList.remove("survey--single-step");

        this.buildSurveySteps();
        this.buildManualLink();
        this.__originalNoResultsText =
            this.resultsContainer?.querySelector(".survey-results__no-results")
                ?.textContent || "";
        if (this.loading) this.loading.style.display = "none";
        if (spinningLoader) spinningLoader.classList.add("hidden");
        if (this.informationContainer)
          this.informationContainer.style.display = "flex";

        this.hideDefaultProductForm();

        try {
          this.tryPrefillFromQuizLocalStorage();
        } catch (e) {
          console.warn("[ProductSurvey] Prefill failed", e);
        }

        if (this.selectedVariants.length === 0) {
          this.startSurvey();
        }

        this.updateBuyButtons();
      } catch (error) {
        this.showFallback();
      }
    }

trackEvent(eventName, properties = {}) {
  try {
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture(eventName, properties);
    }
  } catch (e) {
    // intentionally empty — posthog errors should not break the survey
  }
}

    tryPrefillFromQuizLocalStorage() {
      // Alleen prefillen als er nog geen survey-interactie is geweest
      if (this.answers && Object.keys(this.answers).length > 0) return;

      let raw;
      try {
        raw = localStorage.getItem("iwvmsa_quiz_answers");
      } catch (e) {
        return;
      }
      if (!raw) return;

      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (e) {
        return;
      }

      const quizAnswers = payload && payload.answers ? payload.answers : null;
      if (!quizAnswers || typeof quizAnswers !== "object") return;

      const mapped = {};

      // quiz: man/vrouw/nb -> survey: Man/Vrouw/Anders
      if (typeof quizAnswers.gender === "string") {
        if (quizAnswers.gender === "man") mapped.gender = "Man";
        else if (quizAnswers.gender === "vrouw") mapped.gender = "Vrouw";
        else mapped.gender = "Anders";
      }

      // quiz: contact is multi-select array with values: vaginaal/anaal/oraal/geen_contact
      const contact = Array.isArray(quizAnswers.contact)
          ? quizAnswers.contact
          : [];

      if (contact.length) {
        mapped.vaginal_sex = contact.includes("vaginaal") ? "Ja" : "Nee";
        mapped.oral_sex = contact.includes("oraal") ? "Ja" : "Nee";
        mapped.anal_sex = contact.includes("anaal") ? "Ja" : "Nee";
      }

      // Alleen doorgaan als we minimaal gender + contact-mapping hebben
      if (
          !mapped.gender ||
          !mapped.vaginal_sex ||
          !mapped.oral_sex ||
          !mapped.anal_sex
      )
        return;

      this.answers = mapped;

      // In single-step willen we nooit het overzicht/results scherm zien.
      // We gebruiken de prefill enkel om de selectie + koopknoppen te updaten.
      if (this.isSingleStep) {
        const selected = [];
        if (mapped.vaginal_sex === "Ja") selected.push("vaginaal");
        if (mapped.oral_sex === "Ja") selected.push("oraal");
        if (mapped.anal_sex === "Ja") selected.push("anaal");
        mapped.selected_test_types = selected;

        // Update UI (gender radio + test checkboxes) en onderliggende variant selectie
        try {
          const genderInputs = this.stepContainer?.querySelectorAll(
              'input[name="survey[gender]"]',
          );
          if (genderInputs && genderInputs.length) {
            genderInputs.forEach((el) => {
              el.checked = el.value === mapped.gender;
            });
            // trigger change zodat test opties opnieuw gerenderd worden
            const activeGender = this.stepContainer.querySelector(
                'input[name="survey[gender]"]:checked',
            );
            if (activeGender) activeGender.dispatchEvent(new Event("change"));
          }

          // Na render: zet test checkboxes
          const testInputs = this.stepContainer?.querySelectorAll(
              'input[name="survey[test_type][]"]',
          );
          if (testInputs && testInputs.length) {
            testInputs.forEach((el) => {
              el.checked = selected.includes(el.value);
            });
          }
        } catch (e) {
          // ignore
        }

        this.selectedVariants = this.findMatchingVariants(this.answers);
        this.updateBuyButtons();
        return;
      }

      this.showResults();
    }

    patchCartNotification() {
      setTimeout(() => {
        const cartNotification = document.querySelector("cart-notification");
        if (cartNotification) {
          const originalGetSectionInnerHTML =
              cartNotification.getSectionInnerHTML.bind(cartNotification);

          cartNotification.getSectionInnerHTML = function (
              html,
              selector = ".shopify-section",
          ) {
            try {
              const parsed = new DOMParser().parseFromString(html, "text/html");
              const element = parsed.querySelector(selector);
              return element ? element.innerHTML : "";
            } catch (error) {
              return "";
            }
          };
        }
      }, 100);
    }

    hideDefaultProductForm() {
      const root =
          this.closest('section[id^="MainProduct-"]') ||
          this.closest(".product") ||
          this;

      const variantPicker = root.querySelector(".variant-picker");
      const buyButtons = root.querySelector(".product-form__buttons");

      if (variantPicker) variantPicker.style.display = "none";
      if (buyButtons) buyButtons.style.display = "none";
    }

    showFallback() {
      this.style.display = "none";

      const root =
          this.closest('section[id^="MainProduct-"]') ||
          this.closest(".product") ||
          document;

      const variantPicker = root.querySelector(".variant-picker");
      const buyButtons = root.querySelector(".product-form__buttons");

      if (variantPicker) variantPicker.style.display = "block";
      if (buyButtons) buyButtons.style.display = "block";
    }

    buildSurveySteps() {
      this.stepContainer.innerHTML = "";

      if (this.isSingleStep) {
        this.buildSingleStepVariant();
      } else {
        this.surveyData.questions.forEach((question, questionIndex) => {
          const stepEl = this.createStepElement(question, questionIndex);
          this.stepContainer.appendChild(stepEl);
        });
      }

      // Required boodschap pas tonen na eerste koop-poging zonder geldige selectie
      if (this.requiredMsg) this.requiredMsg.hidden = true;
    }

    buildManualLink() {
      if (!this.informationContainer) return;
      if (this.manualLink) return;

      const manualLink = document.createElement("a");
      manualLink.href = "#";
      manualLink.className = "survey-step__manual-link";
      manualLink.textContent =
        "Weet je al wat je wilt? Kies dan handmatig.";
      manualLink.addEventListener("click", (e) => {
        e.preventDefault();
        this.showManualPicker();
      });

      this.informationContainer.after(manualLink);
      this.manualLink = manualLink;
    }

    buildIntroStep() {
      if (!this.introContainer) return;
      this.introContainer.innerHTML = "";

      const title = document.createElement("p");
      title.className = "survey-step__title";
      title.textContent = "Bepaal welke lichaamslocaties je wilt testen";

      const description = document.createElement("p");
      description.className = "survey-step__description";
      description.textContent =
        "Niet iedere SOA is op elke plek aantoonbaar. Kies daarom eerst hoe je wilt bepalen welke testlocaties voor jou relevant zijn.";

      const optionsDiv = document.createElement("div");
      optionsDiv.className = "survey-options survey-options--radio";

      const options = [
        {
          value: "survey",
          label: "Ontvang een persoonlijk testadvies",
        },
        {
          value: "manual",
          label: "Zelf testlocaties kiezen",
        },
      ];

      options.forEach((option, idx) => {
        const optionDiv = document.createElement("div");
        optionDiv.className = "survey-option";

        const input = document.createElement("input");
        input.type = "radio";
        input.id = `survey-intro-${idx}-${this.dataset.section}`;
        input.name = "survey_intro";
        input.value = option.value;
        input.className = "survey-option__input";

        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "survey-option__label";
        label.textContent = option.label;

        optionDiv.appendChild(input);
        optionDiv.appendChild(label);
        optionsDiv.appendChild(optionDiv);

        input.addEventListener("change", () => {
          if (option.value === "survey") {
            this.startSurvey();
          } else {
            this.startManualPicker();
          }
        });
      });

      this.introContainer.appendChild(title);
      this.introContainer.appendChild(description);
      this.introContainer.appendChild(optionsDiv);
    }

    startSurvey() {
      this.__manualMode = false;
      this.__purchaseAttempted = false;
      if (this.introContainer) this.introContainer.style.display = "none";
      if (this.stepContainer) this.stepContainer.style.display = "block";
      if (this.informationContainer)
        this.informationContainer.style.display = "flex";
      if (this.manualLink) this.manualLink.style.display = "";
      this.showStep(0);
      this.updateBuyButtons();
    }

    startManualPicker() {
      this.__manualMode = true;
      if (this.introContainer) this.introContainer.style.display = "none";
      this.showManualPicker();
    }

    syncSingleStepSelectionFromDom() {
      if (!this.isSingleStep) return;

      const root = this.stepContainer || this;
      if (!root) return;

      const genderEl = root.querySelector(
          'input[name="survey[gender]"]:checked',
      );
      const testEls = root.querySelectorAll(
          'input[name="survey[test_type][]"]:checked',
      );
      const selected = Array.from(testEls).map((el) => el.value);

      if (genderEl) this.answers.gender = genderEl.value;
      this.answers.selected_test_types = selected;
      this.answers.vaginal_sex = selected.includes("vaginaal") ? "Ja" : "Nee";
      this.answers.oral_sex = selected.includes("oraal") ? "Ja" : "Nee";
      this.answers.anal_sex = selected.includes("anaal") ? "Ja" : "Nee";

      if (!this.answers.gender || selected.length === 0) {
        this.selectedVariants = [];
      } else {
        this.selectedVariants = this.findMatchingVariants(this.answers);
      }

      this.updateBuyButtons();
    }

    buildSingleStepVariant() {
      const singleStep = document.createElement("div");
      singleStep.className = "survey-step";
      singleStep.dataset.step = 0;
      singleStep.style.display = "none";
      singleStep.style.padding = "0";
      singleStep.style.background = "transparent";

      const genderTitle = document.createElement("label");
      genderTitle.className = "survey-step__title";
      genderTitle.textContent = "Hoe identificeer jij jezelf?";
      singleStep.appendChild(genderTitle);

      const genderOptionsDiv = document.createElement("div");
      genderOptionsDiv.className = "survey-options survey-options--radio";

      const genders = [
        { label: "Man", value: "Man" },
        { label: "Vrouw", value: "Vrouw" },
        { label: "Anders", value: "Anders" },
      ];

      genders.forEach((g, idx) => {
        const optionDiv = document.createElement("div");
        optionDiv.className = "survey-option";

        const input = document.createElement("input");
        input.type = "radio";
        input.id = `single-step-gender-${idx}`;
        input.name = "survey[gender]";
        input.value = g.value;
        input.className = "survey-option__input";

        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "survey-option__label";
        label.textContent = g.label;

        optionDiv.appendChild(input);
        optionDiv.appendChild(label);
        genderOptionsDiv.appendChild(optionDiv);
      });

      singleStep.appendChild(genderOptionsDiv);

      const testTitle = document.createElement("label");
      testTitle.className = "survey-step__title";
      testTitle.textContent = "Kies jouw soort test(s)";
      singleStep.appendChild(testTitle);

      const testDescription = document.createElement("p");
      testDescription.className = "survey-step__description";
      testDescription.textContent =
          "Je kan meerdere opties kiezen. Selecteer waar je getest wilt worden.";
      singleStep.appendChild(testDescription);

      const optionsDiv = document.createElement("div");
      optionsDiv.className = "survey-options survey-options--checkbox";
      singleStep.appendChild(optionsDiv);

      const allTestOptions = [
        { label: "Anaal", value: "anaal" },
        { label: "Oraal", value: "oraal" },
        { label: "Urine", value: "urine" },
        { label: "Vaginaal", value: "vaginaal" },
      ];

      const getTooltipText = (disabledValue, reason) => {
        if (reason === "mutual_exclusive") {
          if (disabledValue === "urine") {
            return "Je hoeft maar 1 van deze twee te kiezen. Een urinetest is bedoeld voor testen via urine. Als je een vaginale test kiest, is een urinetest meestal niet nodig.";
          }
          if (disabledValue === "vaginaal") {
            return "Je hoeft maar 1 van deze twee te kiezen. Een vaginale test is bedoeld voor testen via een vaginale swab. Als je een urinetest kiest, is een vaginale test meestal niet nodig.";
          }
        }

        if (reason === "gender") {
          if (disabledValue === "vaginaal") {
            return "Deze optie is niet beschikbaar op basis van je selectie bij 'Hoe identificeer jij jezelf?'.";
          }
          if (disabledValue === "urine") {
            return "Deze optie is niet beschikbaar op basis van je selectie bij 'Hoe identificeer jij jezelf?'.";
          }
        }

        if (reason === "out_of_stock") {
          return "Deze optie is momenteel niet op voorraad en kan niet besteld worden.";
        }

        return "";
      };

      const setTooltipState = (checkboxEl, enabled, reason) => {
        const optionEl = checkboxEl?.closest?.(".survey-option");
        if (!optionEl) return;
        if (enabled) {
          optionEl.classList.remove("survey-option--tooltip");
          optionEl.removeAttribute("data-tooltip");
        } else {
          optionEl.classList.add("survey-option--tooltip");
          optionEl.setAttribute(
              "data-tooltip",
              getTooltipText(checkboxEl.value, reason),
          );
        }
      };

      const applyAvailabilityRules = () => {
        const gender = this.answers.gender;
        if (!gender) return;

        optionsDiv
            .querySelectorAll('input[name="survey[test_type][]"]')
            .forEach((input) => {
              // Als er al een andere disable-reason geldt (gender/mutual_exclusive), die tooltip behouden.
              if (input.disabled === true) return;

              const variant = this.findVariant(gender, input.value);
              if (variant && variant.available === false) {
                input.checked = false;
                input.disabled = true;
                setTooltipState(input, false, "out_of_stock");
              } else {
                setTooltipState(input, true);
              }
            });
      };

      const readSelectedTestTypes = () => {
        const checked = optionsDiv.querySelectorAll(
            'input[name="survey[test_type][]"]:checked',
        );
        return Array.from(checked).map((el) => el.value);
      };

      const syncSelection = () => {
        const gender = this.answers.gender;
        const selected = readSelectedTestTypes();

        this.answers.selected_test_types = selected;
        this.answers.vaginal_sex = selected.includes("vaginaal") ? "Ja" : "Nee";
        this.answers.oral_sex = selected.includes("oraal") ? "Ja" : "Nee";
        this.answers.anal_sex = selected.includes("anaal") ? "Ja" : "Nee";

        // In single-step tonen we nooit het results/overzicht scherm.
        // We berekenen enkel de varianten + updaten de koopknoppen.
        if (!gender || selected.length === 0) {
          this.selectedVariants = [];
          this.updateBuyButtons();
          return;
        }

        this.selectedVariants = this.findMatchingVariants(this.answers);
        this.updateBuyButtons();
      };

      const applyGenderRules = (gender) => {
        const urine = optionsDiv.querySelector('input[value="urine"]');
        const vaginaal = optionsDiv.querySelector('input[value="vaginaal"]');

        if (urine) {
          urine.disabled = false;
          setTooltipState(urine, true);
        }
        if (vaginaal) {
          vaginaal.disabled = false;
          setTooltipState(vaginaal, true);
        }

        if (gender === "Man") {
          if (vaginaal) {
            vaginaal.checked = false;
            vaginaal.disabled = true;
            setTooltipState(vaginaal, false, "gender");
          }
        } else if (gender === "Vrouw") {
          if (urine) {
            urine.checked = false;
            urine.disabled = true;
            setTooltipState(urine, false, "gender");
          }
        }
      };

      const applyMutualExclusionForAnders = () => {
        if (this.answers.gender !== "Anders") return;

        const urine = optionsDiv.querySelector('input[value="urine"]');
        const vaginaal = optionsDiv.querySelector('input[value="vaginaal"]');

        if (!urine || !vaginaal) return;

        const urineChecked = urine.checked === true;
        const vaginaalChecked = vaginaal.checked === true;

        if (urineChecked) {
          vaginaal.disabled = true;
          vaginaal.checked = false;
          setTooltipState(vaginaal, false, "mutual_exclusive");
        } else if (vaginaalChecked) {
          urine.disabled = true;
          urine.checked = false;
          setTooltipState(urine, false, "mutual_exclusive");
        } else {
          urine.disabled = false;
          vaginaal.disabled = false;
          setTooltipState(urine, true);
          setTooltipState(vaginaal, true);
        }
      };

      const renderTestOptions = () => {
        optionsDiv.innerHTML = "";

        allTestOptions.forEach((opt) => {
          const optionDiv = document.createElement("div");
          optionDiv.className = "survey-option";

          const input = document.createElement("input");
          input.type = "checkbox";
          input.id = `single-step-opt-${opt.value}`;
          input.name = "survey[test_type][]";
          input.value = opt.value;
          input.className = "survey-option__input";

          const label = document.createElement("label");
          label.htmlFor = input.id;
          label.className = "survey-option__label";
          label.textContent = opt.label;

          input.addEventListener("change", () => {
            applyGenderRules(this.answers.gender);
            applyMutualExclusionForAnders();
            applyAvailabilityRules();
            syncSelection();
          });

          optionDiv.appendChild(input);
          optionDiv.appendChild(label);
          optionsDiv.appendChild(optionDiv);
        });

        applyGenderRules(this.answers.gender);
        applyMutualExclusionForAnders();
        applyAvailabilityRules();
      };

      singleStep
          .querySelectorAll('input[name="survey[gender]"]')
          .forEach((input) => {
            input.addEventListener("change", () => {
              this.answers.gender = input.value;
              renderTestOptions();
              applyAvailabilityRules();
              syncSelection();
            });
          });

      renderTestOptions();

      // Init: zorg dat knoppen/hidden inputs kloppen als er al state is
      syncSelection();

      this.stepContainer.appendChild(singleStep);
    }

    handleSingleStepSubmit() {
      const checked = this.stepContainer.querySelectorAll(
          'input[name="survey[test_type][]"]:checked',
      );
      const selected = Array.from(checked).map((el) => el.value);

      // In de single-step variant kiest de gebruiker direct welke test(s) die wil.
      // We gebruiken deze selectie later om rule-matching (multi-step) te omzeilen.
      this.answers.selected_test_types = selected;

      // Map naar hetzelfde answers-formaat als de multi-step variant
      this.answers.vaginal_sex =
          selected.includes("vaginaal")
              ? "Ja"
              : "Nee";
      this.answers.oral_sex = selected.includes("oraal") ? "Ja" : "Nee";
      this.answers.anal_sex = selected.includes("anaal") ? "Ja" : "Nee";

      // Track survey completion
      this.trackEvent("survey_completed", {
        selected_tests: selected,
        gender: this.answers.gender,
      });

      this.showResults();
    }

    createStepElement(question, questionIndex) {
      const stepDiv = document.createElement("div");
      const totalSteps = this.surveyData.questions.length;

      stepDiv.className = "survey-step";
      stepDiv.dataset.step = questionIndex;
      stepDiv.style.display = "none";

      const label = document.createElement("label");
      label.className = "survey-step__title";
      label.textContent = `${questionIndex + 1}/${totalSteps} - ${
          question.question
      }`;
      if (question.required) label.setAttribute("required", "");
      stepDiv.appendChild(label);

      if (question.description) {
        const description = document.createElement("p");
        description.className = "survey-step__description";
        description.textContent = question.description;
        stepDiv.appendChild(description);
      }

      const optionsDiv = document.createElement("div");
      optionsDiv.className =
          question.type === "radio"
              ? "survey-options survey-options--radio"
              : "survey-options survey-options--checkbox";

      question.options.forEach((option, optionIndex) => {
        const optionEl = this.createStepOptionElement(
            question,
            option,
            optionIndex,
            questionIndex,
        );
        optionsDiv.appendChild(optionEl);
      });

      stepDiv.appendChild(optionsDiv);
      return stepDiv;
    }

    createStepOptionElement(question, option, optionIndex, questionIndex) {
      const optionDiv = document.createElement("div");
      optionDiv.className = "survey-option";

      const input = document.createElement("input");
      input.type = question.type;
      input.id = `q-${question.id}-${optionIndex}`;
      input.name =
          question.type === "radio"
              ? `survey[${question.id}]`
              : `survey[${question.id}][]`;
      input.value = option;
      input.className = "survey-option__input";
      if (question.required) input.setAttribute("required", "");

      input.addEventListener("change", () =>
          this.handleOptionChange(question, option, questionIndex),
      );

      const label = document.createElement("label");
      label.htmlFor = input.id;
      label.className = "survey-option__label";
      label.textContent = option;

      optionDiv.appendChild(input);
      optionDiv.appendChild(label);

      return optionDiv;
    }

    handleOptionChange(question, option, questionIndex) {
      this.answers[question.id] = option;

      if (questionIndex === this.surveyData.questions.length - 1) {
        // Track survey completion voor multi_step variant
        this.trackEvent("survey_completed", {
          gender: this.answers.gender,
        });
        setTimeout(() => this.showResults(), 300);
      } else {
        setTimeout(() => this.showStep(questionIndex + 1), 300);
      }
    }

    showStep(stepIndex) {
      const currentStepEl = this.querySelector(
          `[data-step="${this.currentStep}"]`,
      );
      if (currentStepEl) {
        currentStepEl.style.display = "none";
        currentStepEl.classList.remove("active");
      }

      const newStepEl = this.querySelector(`[data-step="${stepIndex}"]`);
      if (newStepEl) {
        newStepEl.style.display = "block";
        newStepEl.classList.add("active");
      }

      this.currentStep = stepIndex;
    }

    showResults() {
      if (this.introContainer) this.introContainer.style.display = "none";
      if (this.stepContainer) this.stepContainer.style.display = "none";
      if (this.informationContainer)
        this.informationContainer.style.display = "none";
      if (this.manualLink) this.manualLink.style.display = "none";

      const suggestedVariants = this.findMatchingVariants(this.answers);
      this.selectedVariants = [...suggestedVariants];

      // Track quiz results viewed
      this.trackEvent("quiz_results_viewed", {
        variant_count: suggestedVariants.length,
        variant_ids: suggestedVariants.map((v) => v.id),
        variant_titles: suggestedVariants.map((v) => v.title),
        gender: this.answers.gender,
        from_quiz: true,
      });

      this.displayResults(this.selectedVariants);
      if (this.resultsContainer) this.resultsContainer.style.display = "block";
    }

    findMatchingVariants(answers) {
      const matchingTestTypes = new Set();

      // Single-step variant: user selected test types directly.
      if (
          answers &&
          Array.isArray(answers.selected_test_types) &&
          answers.selected_test_types.length
      ) {
        return this.findVariantsByTestTypes(
            answers.gender,
            answers.selected_test_types,
        );
      }

      for (let rule of this.surveyData.rules) {
        if (this.matchesRule(answers, rule.conditions)) {
          rule.test_types.forEach((type) => matchingTestTypes.add(type));
        }
      }

      return this.findVariantsByTestTypes(
          answers.gender,
          Array.from(matchingTestTypes),
      );
    }

    findVariantsByTestTypes(gender, testTypes) {
      const matchingVariants = [];

      for (let testType of testTypes) {
        let variant = this.findVariant(gender, testType);

        if (variant) {
          const isAvailable = variant.available !== false;
          if (isAvailable) {
            matchingVariants.push(variant);
          } else if (variant.sku) {
            const alternativeVariant = this.findAlternativeVariantBySku(
                variant.sku,
            );
            if (alternativeVariant) {
              matchingVariants.push(alternativeVariant);
            } else {
              matchingVariants.push(variant);
            }
          } else {
            matchingVariants.push(variant);
          }
        }
      }

      return matchingVariants;
    }

    findAlternativeVariantBySku(sku) {
      return this.productData.variants.find((variant) => {
        return variant.sku === sku && variant.available !== false;
      });
    }

    findVariant(gender, testType) {
      return this.productData.variants.find((variant) => {
        const option1 = variant.option1;
        const option2 = variant.option2;
        const option3 = variant.option3;
        const title = variant.title;

        const toCanonicalGender = (value) => {
          if (!value) return "";
          const v = String(value).trim().toLowerCase();
          if (v.includes("vrouw")) return "vrouw";
          if (v.includes("dame")) return "vrouw";
          if (v.includes("female")) return "vrouw";
          if (v.includes("man")) return "man";
          if (v.includes("heer")) return "man";
          if (v.includes("male")) return "man";
          if (v.includes("anders")) return "anders";
          return v;
        };

        const toCanonicalTestType = (value) => {
          if (!value) return "";
          const v = String(value).toLowerCase();
          if (v.includes("urine")) return "urine";
          if (v.includes("vagina")) return "vaginaal";
          if (v.includes("oraal")) return "oraal";
          if (v.includes("anaal")) return "anaal";
          return v;
        };

        const canonicalGender = toCanonicalGender(gender);
        const canonicalRuleType = toCanonicalTestType(testType);

        const options = [option1, option2, option3].filter(Boolean);

        const optionGenders = options.map((o) => toCanonicalGender(o));
        const optionTypes = options.map((o) => toCanonicalTestType(o));

        const o1Gender = optionGenders[0] || "";
        const o2Gender = optionGenders[1] || "";
        const o3Gender = optionGenders[2] || "";
        const o1Type = optionTypes[0] || "";
        const o2Type = optionTypes[1] || "";
        const o3Type = optionTypes[2] || "";

        // Shopify kan optie-volgorde wisselen (gender/testtype). Detecteer dit heuristisch.
        const canonicalVariantGender =
            o1Gender === "man" || o1Gender === "vrouw"
                ? o1Gender
                : o2Gender === "man" || o2Gender === "vrouw"
                    ? o2Gender
                    : o3Gender === "man" || o3Gender === "vrouw"
                        ? o3Gender
                        : "";

        const canonicalVariantType =
            o1Type === "urine" ||
            o1Type === "vaginaal" ||
            o1Type === "oraal" ||
            o1Type === "anaal"
                ? o1Type
                : o2Type === "urine" ||
                o2Type === "vaginaal" ||
                o2Type === "oraal" ||
                o2Type === "anaal"
                    ? o2Type
                    : o3Type === "urine" ||
                    o3Type === "vaginaal" ||
                    o3Type === "oraal" ||
                    o3Type === "anaal"
                        ? o3Type
                        : "";

        // Fallback naar legacy assumptie (option1=gender, option2=testtype)
        const resolvedVariantGender =
            canonicalVariantGender || toCanonicalGender(option1);
        const resolvedVariantType =
            canonicalVariantType || toCanonicalTestType(option2);

        // Extra fallback: Shopify titles bevatten vaak de echte waarden
        const haystack = `${option1 || ""} ${option2 || ""} ${option3 || ""} ${title || ""}`;
        const titleGender = toCanonicalGender(haystack);
        const titleType = toCanonicalTestType(haystack);

        const finalVariantGender = resolvedVariantGender || titleGender;
        const finalVariantType = resolvedVariantType || titleType;

        if (finalVariantType !== canonicalRuleType) return false;

        if (canonicalGender === "anders") {
          // Productvarianten zijn gendered (Man/Vrouw), maar bij 'Anders' kiezen we
          // de meest logische variant per testtype.
          if (canonicalRuleType === "vaginaal") {
            return finalVariantGender === "vrouw";
          }
          return finalVariantGender === "man";
        } else {
          // Als de variant geen herkenbare gender bevat, match enkel op type.
          if (!finalVariantGender) return true;
          return finalVariantGender === canonicalGender;
        }
      });
    }

    matchesRule(answers, conditions) {
      for (let [key, expectedValue] of Object.entries(conditions)) {
        const actualValue = answers[key];

        if (Array.isArray(expectedValue)) {
          if (!actualValue || !Array.isArray(actualValue)) return false;
          if (!expectedValue.some((val) => actualValue.includes(val)))
            return false;
        } else {
          if (actualValue !== expectedValue) return false;
        }
      }

      return true;
    }

    formatMoney(cents) {
      const format =
          window.theme?.moneyFormat || "€{{amount_with_comma_separator}}";
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, format);
      }
      // fallback
      const euros = (cents / 100).toFixed(2).replace(".", ",");
      return `${euros}`;
    }

    getDiscountPctForIndex(oneBasedIndex) {
      if (oneBasedIndex >= 2) return 0.20;
      return 0;
    }

    getDiscountedCents(priceCents, pct) {
      return Math.round(priceCents * (1 - pct));
    }

    ensureResultsTotalRow() {
      let totalEl = this.resultsContainer.querySelector(
          ".survey-results__total",
      );
      if (totalEl) return totalEl;

      totalEl = document.createElement("div");
      totalEl.className = "survey-results__total";

      totalEl.innerHTML = `
        <div class="survey-results__totals">

          <div class="survey-results__totals-row" data-row-subtotal>
            <span class="survey-results__totals-label">Subtotaal:</span>
            <span class="survey-results__totals-value" data-survey-subtotal></span>
          </div>

          <div class="survey-results__totals-row survey-results__totals-row--perks">
            <span class="survey-results__totals-label">Medische begeleiding:</span>
            <span class="survey-results__totals-value" data-survey-perk-medical></span>
          </div>

          <div class="survey-results__totals-row survey-results__totals-row--discount" data-row-discount>
            <span class="survey-results__totals-label">Korting:</span>
            <span class="survey-results__totals-value" data-survey-discount></span>
          </div>

          <div class="survey-results__totals-row survey-results__totals-row--total">
            <span class="survey-results__totals-label">Huidige prijs:</span>
            <span class="survey-results__totals-value" data-survey-total></span>
          </div>
        </div>
      `;

      this.variantsContainer.insertAdjacentElement("afterend", totalEl);
      return totalEl;
    }

    updateResultsTotal() {
      const totalWrap = this.ensureResultsTotalRow();

      const subtotalEl = totalWrap.querySelector("[data-survey-subtotal]");
      const perkMedicalEl = totalWrap.querySelector("[data-survey-perk-medical]");
      const discountEl = totalWrap.querySelector("[data-survey-discount]");
      const totalEl = totalWrap.querySelector("[data-survey-total]");
      const discountRow = totalWrap.querySelector("[data-row-discount]");

      const available = this.selectedVariants.filter((v) => v.available !== false);

      // Subtotaal = normale productprijzen
      // Items zonder bundelkorting: gebruik compare_at_price (bijv. 130)
      // Items met bundelkorting: gebruik price als basis (bijv. 109, waarop 20% geldt)
      const variantsSubtotal = available.reduce((sum, v, idx) => {
        const pct = this.getDiscountPctForIndex(idx + 1);
        const normalPrice = pct > 0
          ? (v.price || 0)
          : ((v.compare_at_price && v.compare_at_price > v.price) ? v.compare_at_price : (v.price || 0));
        return sum + normalPrice;
      }, 0);

      // Totaal = met survey-korting vanaf product 2
      const variantsTotal = available.reduce((sum, v, idx) => {
        const base = v.price || 0;
        const pct = this.getDiscountPctForIndex(idx + 1);
        return sum + this.getDiscountedCents(base, pct);
      }, 0);

      // Gratis perks (hardcoded values)
      const medicalValue = 2500; // Medische nazorg (25,00)

      const subtotal = variantsSubtotal; // Only products, not perks
      const total = variantsTotal;
      const discount = variantsSubtotal - variantsTotal; // Only product discount, not perks

      subtotalEl.textContent = this.formatMoney(subtotal);
      perkMedicalEl.innerHTML = `<span class="survey-results__totals-value--gratis">GRATIS</span>`;
      totalEl.textContent = this.formatMoney(total);

      if (discount > 0) {
        discountEl.textContent = `−${this.formatMoney(discount)}`;
        discountRow.style.display = "";
      } else {
        discountRow.style.display = "none";
      }
    }

    isNoSexCase() {
      return (
          this.answers.vaginal_sex === "Nee" &&
          this.answers.oral_sex === "Nee" &&
          this.answers.anal_sex === "Nee"
      );
    }

    toggleFallback(show) {
      const fallbackEl = this.resultsContainer.querySelector(
          ".survey-results__fallback",
      );
      if (!fallbackEl) return;
      fallbackEl.style.display = show ? "block" : "none";
    }

    displayResults(variants) {
      this.variantsContainer.innerHTML = "";
      const resultsTitle = this.resultsContainer.querySelector(
          ".survey-results__title",
      );
      const noResultsText = this.resultsContainer.querySelector(
          ".survey-results__no-results",
      );

      if (variants.length === 0) {
        resultsTitle.style.display = "none";

        const totalEl = this.resultsContainer.querySelector(
            ".survey-results__total",
        );
        if (totalEl) totalEl.style.display = "none";

        if (this.__manualMode) {
          noResultsText.textContent = this.__manualEmptyMsg;
          noResultsText.style.display = "block";
          this.toggleFallback(false);
        } else if (this.isNoSexCase()) {
          noResultsText.style.display = "none";
          this.toggleFallback(true);
        } else {
          noResultsText.textContent = this.__originalNoResultsText;
          noResultsText.style.display = "block";
          this.toggleFallback(false);
        }
      } else {
        this.toggleFallback(false);
        if (this.__manualMode) {
          resultsTitle.textContent = "Jouw gekozen test(en)";
          resultsTitle.style.display = "block";
        } else {
          resultsTitle.style.display = "block";
        }
        noResultsText.style.display = "none";
        variants.forEach((variant, index) => {
          const variantListItem = this.createVariantListItem(variant, index);
          this.variantsContainer.appendChild(variantListItem);
        });

        this.updateBuyButtons();
        this.updateResultsTotal();
        
        // Show total section
        const totalEl = this.resultsContainer.querySelector(
            ".survey-results__total",
        );
        if (totalEl) totalEl.style.display = "block";
      }
    }

    createVariantListItem(variant, index) {
      const listItem = document.createElement("li");
      const isAvailable = variant.available !== false;
      listItem.className = isAvailable
          ? "survey-variant"
          : "survey-variant unavailable";
      listItem.dataset.variantId = variant.id;

      if (!isAvailable) {
        listItem.setAttribute("title", "Uitverkocht");
      }

      const displayTitle = this.getProductDisplayTitle(
          variant.option2 || variant.title,
      );

      const priceCents = variant.price || 0;
      const surveyDiscountPct = this.getDiscountPctForIndex(index + 1);
      const discountedPriceCents = this.getDiscountedCents(priceCents, surveyDiscountPct);

      const price = this.formatMoney(discountedPriceCents);
      const compareBaseCents = surveyDiscountPct > 0
        ? priceCents
        : ((variant.compare_at_price && variant.compare_at_price > priceCents) ? variant.compare_at_price : priceCents);
      const compareAt = this.formatMoney(compareBaseCents);

      let priceHtml = `<span class="survey-variant__price-new">${price}</span>`;

      if (surveyDiscountPct > 0) {
        priceHtml = `
          <span class="survey-variant__price-old">${compareAt}</span>
          <span class="survey-variant__price-new">${price}</span>
        `;
      } else if ((variant.compare_at_price || 0) > priceCents) {
        priceHtml = `
          <span class="survey-variant__price-old">${this.formatMoney(variant.compare_at_price)}</span>
          <span class="survey-variant__price-new">${this.formatMoney(priceCents)}</span>
        `;
      }

      const hasDiscount = surveyDiscountPct > 0;
      const discountText = hasDiscount 
        ? `Bij het bestellen van meerdere testen krijg je automatisch ${surveyDiscountPct * 100}% bundelkorting op de 2de en 3de test variant.`
        : '';

      listItem.innerHTML = `
        <div class="survey-variant__title">
          <div class="survey-variant__title-text">
            <div class="survey-variant__title-main">
              1 x ${displayTitle}
              ${hasDiscount ? `<span class="survey-variant__discount-icon" data-discount-text="${discountText}" aria-label="20% korting"></span>` : ''}
            </div>
          </div>
        </div>

        <div class="survey-variant__price">
          ${priceHtml}
        </div>

        <button class="survey-variant__remove-btn" data-variant-id="${variant.id}" title="Verwijder uit selectie">
          <span class="visually-hidden">Verwijder ${displayTitle} uit selectie</span>
        </button>
      `;

      listItem
        .querySelector(".survey-variant__remove-btn")
        .addEventListener("click", (e) => {
          e.stopPropagation();
          this.removeVariant(variant.id);
        });

      // Mobile popover toggle voor discount icon (alleen op touch devices)
      if (hasDiscount) {
        const discountIcon = listItem.querySelector(".survey-variant__discount-icon");
        if (discountIcon) {
          // Check of het een touch device is
          const isTouch = window.matchMedia && 
            window.matchMedia("(hover: none), (pointer: coarse)").matches;
          
          if (isTouch) {
            discountIcon.addEventListener("click", (e) => {
              e.stopPropagation();
              e.preventDefault();
              // Toggle active class voor mobiel
              const isActive = discountIcon.classList.contains("active");
              // Sluit alle andere open popovers
              document.querySelectorAll(".survey-variant__discount-icon.active").forEach(icon => {
                icon.classList.remove("active");
              });
              // Toggle huidige popover
              if (!isActive) {
                discountIcon.classList.add("active");
              }
            });
          }
        }
      }

      return listItem;
    }

    removeVariant(variantId) {
      this.selectedVariants = this.selectedVariants.filter(
          (variant) => variant.id !== variantId,
      );

      if (this.__manualMode) {
        const checkbox = this.querySelector(`#manual-variant-${variantId}`);
        if (checkbox) checkbox.checked = false;

        this.updateManualPickerPrices();

        if (this.selectedVariants.length === 0) {
          this.displayResults([]);
          this.updateBuyButtons();
        } else {
          this.displayResults(this.selectedVariants);
        }
        if (this.resultsContainer)
          this.resultsContainer.style.display = "block";
        return;
      }

      if (this.selectedVariants.length === 0) {
        this.resetSurvey();
      } else {
        this.displayResults(this.selectedVariants);
      }
    }

    updateManualPickerPrices() {
      this.querySelectorAll(".survey-manual-picker__input").forEach((input) => {
        const variantId = Number(input.value);
        const variant = (this.productData.variants || []).find(
            (v) => v.id === variantId,
        );
        if (!variant) return;

        const label = input.nextElementSibling;
        if (!label) return;

        const priceSpan = label.querySelector(
            ".survey-manual-picker__label-price",
        );
        if (!priceSpan) return;

        const firstSelected = this.selectedVariants[0];
        const hasSelection = this.selectedVariants.length > 0;
        const isFirstSelected = hasSelection && variant.id === firstSelected.id;

        if (hasSelection && !isFirstSelected) {
          const discounted = this.getDiscountedCents(
              variant.price || 0,
              0.20,
          );
          priceSpan.innerHTML = `
            <span class="survey-manual-picker__price-old">${this.formatMoney(
                variant.price,
            )}</span>
            <span class="survey-manual-picker__price-new">${this.formatMoney(
                discounted,
            )}</span>
          `;
        } else {
          priceSpan.textContent = this.formatMoney(variant.price || 0);
        }
      });
    }

    showManualPicker() {
      this.__manualMode = true;
      this.__purchaseAttempted = false;

      const configurator = this.querySelector(".survey-configurator__container");
      if (configurator) {
        configurator.style.display = "block";
      }
      if (this.introContainer) this.introContainer.style.display = "none";
      if (this.stepContainer) this.stepContainer.style.display = "none";
      if (this.loading) this.loading.style.display = "none";
      if (this.informationContainer)
        this.informationContainer.style.display = "none";
      if (this.manualLink) this.manualLink.style.display = "none";

      if (this.requiredMsg) this.requiredMsg.hidden = true;

      const existing = this.querySelector(".survey-manual-picker");
      if (existing) existing.remove();

      const availableVariants = (this.productData.variants || []).filter(
          (v) => v.available !== false,
      );

      const picker = document.createElement("div");
      picker.className = "survey-manual-picker";

      const heading = document.createElement("p");
      heading.className = "survey-step__title";
      heading.textContent = "Selecteer één of meer testen. Vanaf de 2e test krijg je 20% korting.";
      picker.appendChild(heading);

      const list = document.createElement("div");
      list.className = "survey-manual-picker__list";

      availableVariants.forEach((variant) => {
        const optionDiv = document.createElement("div");
        optionDiv.className = "survey-manual-picker__option";

        const input = document.createElement("input");
        input.type = "checkbox";
        input.id = `manual-variant-${variant.id}`;
        input.value = String(variant.id);
        input.className = "survey-manual-picker__input";

        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "survey-manual-picker__label";

        const titleSpan = document.createElement("span");
        titleSpan.className = "survey-manual-picker__label-title";
        titleSpan.textContent = variant.title;

        const priceSpan = document.createElement("span");
        priceSpan.className = "survey-manual-picker__label-price";
        priceSpan.textContent = this.formatMoney(variant.price || 0);

        label.appendChild(titleSpan);
        label.appendChild(priceSpan);
        optionDiv.appendChild(input);
        optionDiv.appendChild(label);
        list.appendChild(optionDiv);

        input.addEventListener("change", () => {
          const checked = picker.querySelectorAll(
              ".survey-manual-picker__input:checked",
          );
          this.selectedVariants = Array.from(checked)
              .map((el) =>
                  (this.productData.variants || []).find(
                      (v) => String(v.id) === el.value,
                  ),
              )
              .filter(Boolean);

          this.updateManualPickerPrices();

          if (this.selectedVariants.length > 0) {
            this.displayResults(this.selectedVariants);
          } else {
            this.displayResults([]);
            this.updateBuyButtons();
          }
          if (this.resultsContainer)
            this.resultsContainer.style.display = "block";
        });
      });

      picker.appendChild(list);

      const buyButtons = this.querySelector(".survey-buy-buttons");
      if (configurator && this.resultsContainer) {
        configurator.insertBefore(picker, this.resultsContainer);

        const divider = document.createElement("div");
        divider.className = "survey-manual-picker__divider";
        configurator.insertBefore(divider, this.resultsContainer);
      } else {
        this.insertBefore(picker, buyButtons);
      }

      this.selectedVariants = [];
      this.updateBuyButtons();
      this.displayResults([]);
      if (this.resultsContainer)
        this.resultsContainer.style.display = "block";
    }

    resetSurvey() {
      this.currentStep = 0;
      this.answers = {};
      this.selectedVariants = [];
      this.__purchaseAttempted = false;
      this.__manualMode = false;

      const configurator = this.querySelector(".survey-configurator__container");
      if (configurator) configurator.style.display = "block";

      const manualPicker = this.querySelector(".survey-manual-picker");
      if (manualPicker) manualPicker.remove();

      if (this.resultsContainer) this.resultsContainer.style.display = "none";
      if (this.stepContainer) this.stepContainer.style.display = "none";
      if (this.informationContainer)
        this.informationContainer.style.display = "flex";
      if (this.introContainer) this.introContainer.style.display = "block";

      this.buildSurveySteps();
      this.startSurvey();

      this.trackEvent("survey_reset");
    }

    canPurchase() {
      if (!this.__manualMode) this.syncSingleStepSelectionFromDom();
      console.log("[Survey canPurchase]", {
        isSingleStep: this.isSingleStep,
        answers: this.answers,
        selectedVariants: this.selectedVariants,
        localStorageQuiz: localStorage.getItem("iwvmsa_quiz_answers")
      });
      const availableVariants = this.selectedVariants.filter(
          (variant) => variant.available !== false,
      );
      return availableVariants.length > 0;
    }

    nudgeSurveyRequired() {
      const manualPicker = this.querySelector(".survey-manual-picker");
      if (this.__manualMode && manualPicker) {
        manualPicker.classList.remove("survey-shake");
        void manualPicker.offsetWidth;
        manualPicker.classList.add("survey-shake");
        window.clearTimeout(this.__shakeTimeout);
        this.__shakeTimeout = window.setTimeout(() => {
          manualPicker.classList.remove("survey-shake");
        }, 600);
        return;
      }

      if (this.requiredMsg) this.requiredMsg.hidden = false;

      const target =
          this.stepContainer && this.stepContainer.style.display !== "none"
              ? this.stepContainer
              : this.resultsContainer &&
              this.resultsContainer.style.display !== "none"
                  ? this.resultsContainer
                  : this.querySelector(".survey-configurator__container") || this;

      if (target) {
        target.classList.remove("survey-shake");
        // force reflow
        void target.offsetWidth;
        target.classList.add("survey-shake");

        window.clearTimeout(this.__shakeTimeout);
        this.__shakeTimeout = window.setTimeout(() => {
          target.classList.remove("survey-shake");
        }, 600);
      }
    }

    updateBuyButtons() {
      const availableVariants = this.selectedVariants.filter(
          (variant) => variant.available !== false,
      );
      const hasVariants = availableVariants.length > 0;

      this.updateVariantInputs();

      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.setAttribute("aria-disabled", "false");
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = false;
        this.buyNowBtn.setAttribute("aria-disabled", "false");
      }

      if (this.requiredMsg) {
        if (
        hasVariants ||
        this.__manualMode ||
        (this.introContainer &&
          this.introContainer.style.display !== "none")
      ) {
        this.requiredMsg.hidden = true;
      } else {
        // Required-boodschap alleen tonen na een kooppoging zonder geldige selectie
        this.requiredMsg.hidden = !this.__purchaseAttempted;
      }
      }

      const addToCartText = this.addToCartBtn
          ? this.addToCartBtn.querySelector(".survey-btn__text")
          : null;
      const buyNowText = this.buyNowBtn
          ? this.buyNowBtn.querySelector(".survey-btn__text")
          : null;

      const setBtnText = (btn, textEl, value) => {
        if (textEl) {
          textEl.textContent = value;
          return;
        }
        if (btn) {
          // Fallback: probeer een label te zetten zonder de spinner markup te slopen
          btn.setAttribute("aria-label", value);
        }
      };

      if (hasVariants) {
        setBtnText(
            this.addToCartBtn,
            addToCartText,
            `Aan winkelwagen toevoegen (${availableVariants.length})`,
        );
        setBtnText(
            this.buyNowBtn,
            buyNowText,
            `Direct bestellen (${availableVariants.length})`,
        );
      } else {
        setBtnText(
            this.addToCartBtn,
            addToCartText,
            "Aan winkelwagen toevoegen",
        );
        setBtnText(this.buyNowBtn, buyNowText, "Direct bestellen");
      }
    }

    updateVariantInputs() {
      if (!this.variantInputsContainer) return;

      this.variantInputsContainer.innerHTML = "";

      const dummyInput = this.surveyForm.querySelector('[name="id"]');
      if (dummyInput) {
        dummyInput.disabled = true;
      }

      const availableVariants = this.selectedVariants.filter(
          (variant) => variant.available !== false,
      );

      let itemIndex = 0;

      availableVariants.forEach((variant) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `items[${itemIndex}][id]`;
        input.value = variant.id;
        input.className = "survey-variant-input";

        const quantityInput = document.createElement("input");
        quantityInput.type = "hidden";
        quantityInput.name = `items[${itemIndex}][quantity]`;
        quantityInput.value = "1";
        quantityInput.className = "survey-quantity-input";

        if (this.answers && this.answers.gender === "Anders") {
          const hideGenderPropertyInput = document.createElement("input");
          hideGenderPropertyInput.type = "hidden";
          hideGenderPropertyInput.name = `items[${itemIndex}][properties][_hide_gender]`;
          hideGenderPropertyInput.value = "1";
          hideGenderPropertyInput.className = "survey-property-input";
          this.variantInputsContainer.appendChild(hideGenderPropertyInput);
        }

        this.variantInputsContainer.appendChild(input);
        this.variantInputsContainer.appendChild(quantityInput);
        itemIndex++;
      });
    }

    async addToCart() {
      if (!this.__manualMode) this.syncSingleStepSelectionFromDom();
      if (this.selectedVariants.length === 0) return;
      if (this.addToCartBtn?.getAttribute("aria-disabled") === "true") return;

      // Track add to cart
      const availableVariants = this.selectedVariants.filter((v) => v.available !== false);
      this.trackEvent("survey_add_to_cart", {
        variant_count: availableVariants.length,
        variant_ids: availableVariants.map((v) => v.id),
        variant_titles: availableVariants.map((v) => v.title),
        gender: this.answers.gender,
        from_quiz: true,
      });

      try {
        // Check of prioriteit checkbox is aangevinkt - voeg deze EERST toe via Cart API
        const priorityCheckbox = this.querySelector('.js-priority-toggle');
        if (priorityCheckbox && priorityCheckbox.checked) {
          // Gebruik de variant ID van het prioriteitsverwerking product (zoals in cart-subtotal.liquid)
          const priorityVariantId = 56685843251535;
          
          try {
            // Voeg prioriteit variant toe via Cart API (zoals in theme.liquid)
            await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: priorityVariantId,
                quantity: 1,
                properties: {
                  'Voor': this.productData.title || ''
                }
              })
            });
          } catch (e) {
            // Silently fail if priority variant cannot be added
          }
        }

        // Update variant inputs ZONDER prioriteit (die is al toegevoegd)
        this.updateVariantInputs();

        const cart =
            document.querySelector("cart-notification") ||
            document.querySelector("cart-drawer");
        if (cart) {
          this.surveyForm
              .querySelectorAll('[name="sections"], [name="sections_url"]')
              .forEach((input) => input.remove());

          const sections = cart
              .getSectionsToRender()
              .map((section) => section.id);

          const sectionsInput = document.createElement("input");
          sectionsInput.type = "hidden";
          sectionsInput.name = "sections";
          sectionsInput.value = sections.join(",");
          this.surveyForm.appendChild(sectionsInput);

          const sectionsUrlInput = document.createElement("input");
          sectionsUrlInput.type = "hidden";
          sectionsUrlInput.name = "sections_url";
          sectionsUrlInput.value = window.location.pathname;
          this.surveyForm.appendChild(sectionsUrlInput);

          cart.setActiveElement(document.activeElement);
        }

        if (this.productForm && this.productForm.onSubmitHandler) {
          const submitEvent = new Event("submit", {
            cancelable: true,
            bubbles: true,
          });
          this.surveyForm.dispatchEvent(submitEvent);
        } else {
          this.surveyForm.submit();
        }
      } catch (error) {
        console.error('[Survey addToCart] Error:', error);
      }
    }

    async buyNow() {
      if (!this.__manualMode) this.syncSingleStepSelectionFromDom();
      if (this.selectedVariants.length === 0) return;

      // Track buy now
      const availableVariants = this.selectedVariants.filter((v) => v.available !== false);
      this.trackEvent("survey_buy_now", {
        variant_count: availableVariants.length,
        variant_ids: availableVariants.map((v) => v.id),
        variant_titles: availableVariants.map((v) => v.title),
        gender: this.answers.gender,
        from_quiz: true,
      });

      try {
        await this.addToCart();
        setTimeout(() => {
          window.location.href = "/cart";
        }, 1500);
      } catch (error) {
        // Error handling via product-form.js
      }
    }

    getProductDisplayTitle(title) {
      const lowerTitle = title.toLowerCase();

      for (const [key, value] of Object.entries(ProductSurvey.VARIANT_TITLES)) {
        if (lowerTitle.includes(key)) {
          return value;
        }
      }

      return title;
    }
  }

  if (!customElements.get("product-survey")) {
    customElements.define("product-survey", ProductSurvey);
  }

  window.ProductSurvey = ProductSurvey;

  if (!customElements.get("product-survey")) {
    customElements.define("product-survey", ProductSurvey);
  }
})();
